import { useState, useEffect } from "react";

export default function TrainingStats({ id }) {
  const [taskData, setTaskData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTask = async () => {
    setLoading(true);
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/trainingtasksidv/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',

      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const tasks = Array.isArray(result.data) ? result.data : [];

      // Group tasks by Task name and count occurrences
      const taskMap = tasks.reduce((acc, curr) => {
        const { Task, Issue, Role, Description } = curr;
        if (!acc[Task]) {
          acc[Task] = { count: 0, issues: [], role: Role, description: Description };
        }
        acc[Task].count += 1;
        if (Issue && !acc[Task].issues.includes(Issue)) {
          acc[Task].issues.push(Issue);
        }
        return acc;
      }, {});

      // Convert to array format for rendering
      const formattedData = Object.entries(taskMap).map(([task, details]) => ({
        task,
        count: details.count,
        description: details.description,
        issues: details.issues,
        role: details.role,
      }));

      setTaskData(formattedData);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTask();
  }, [id]);

  return (
    <div
      data-component="training-suggestions-panel"
      className="p-3 sm:p-4 lg:p-6 bg-white rounded-lg shadow-lg w-full max-w-full overflow-hidden"
    >
      {/* Header Section */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 mb-2 flex items-center">
          🎯 Task Training Suggestions
        </h1>
        <p className="text-xs sm:text-sm text-gray-600">
          Training recommendations based on task performance and issues
        </p>
      </div>

      {/* Action Buttons */}
      <div className="mb-4 sm:mb-6">
        <button
          data-component="training-suggestions-refresh"
          data-feature="fetch-latest"
          onClick={fetchTask}
          className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading...
            </span>
          ) : (
            <span className="flex items-center justify-center sm:justify-start">
              🔄 Refresh Data
            </span>
          )}
        </button>
      </div>

      {/* Mobile and Desktop Data Display */}
      <div className="space-y-4">
        {/* Mobile Card View */}
        <div className="block sm:hidden space-y-4">
          {taskData.length > 0 ? (
            taskData.map((taskItem, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-200">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-bold text-gray-900 text-sm flex-1 pr-2">
                    📋 {taskItem.task}
                  </h3>
                  <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2 py-1 rounded-full flex-shrink-0">
                    #{taskItem.count}
                  </span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="text-xs font-medium text-gray-600 w-16">👤 Role:</span>
                    <span className="text-xs text-gray-800 bg-blue-50 px-2 py-1 rounded">
                      {taskItem.role}
                    </span>
                  </div>
                  
                  <div>
                    <span className="text-xs font-medium text-gray-600">⚠️ Issues:</span>
                    <div className="mt-1">
                      {taskItem.issues.length > 0 ? (
                        <div className="space-y-1">
                          {taskItem.issues.map((issue, idx) => (
                            <div key={idx} className="bg-red-50 border border-red-200 rounded-md p-2">
                              <span className="text-xs text-red-800">{issue}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                          ✅ No Issues
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <span className="text-gray-500 text-sm">📭 No tasks found</span>
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden sm:block">
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-300 bg-white">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-3 sm:py-4 pl-4 pr-3 text-left text-xs sm:text-sm font-bold text-gray-900">
                    📋 Task Name
                  </th>
                  <th className="px-3 py-3 sm:py-4 text-left text-xs sm:text-sm font-bold text-gray-900">
                    🔢 Count
                  </th>
                  <th className="px-3 py-3 sm:py-4 text-left text-xs sm:text-sm font-bold text-gray-900">
                    👤 Role
                  </th>
                  <th className="px-3 py-3 sm:py-4 text-left text-xs sm:text-sm font-bold text-gray-900">
                    ⚠️ Issue(s)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {taskData.length > 0 ? (
                  taskData.map((taskItem, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="py-3 sm:py-4 pl-4 pr-3 text-xs sm:text-sm font-medium text-gray-900">
                        <div className="max-w-xs truncate" title={taskItem.task}>
                          {taskItem.task}
                        </div>
                      </td>
                      <td className="px-3 py-3 sm:py-4 text-xs sm:text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {taskItem.count}
                        </span>
                      </td>
                      <td className="px-3 py-3 sm:py-4 text-xs sm:text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {taskItem.role}
                        </span>
                      </td>
                      <td className="px-3 py-3 sm:py-4 text-xs sm:text-sm max-w-xs">
                        {taskItem.issues.length > 0 ? (
                          <div className="space-y-1">
                            {taskItem.issues.map((issue, idx) => (
                              <div key={idx} className="bg-red-50 border border-red-200 rounded-md px-2 py-1">
                                <span className="text-xs text-red-700">{issue}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            ✅ No Issues
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-8">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <span className="text-4xl">📭</span>
                        <span className="text-sm text-gray-500">No tasks found</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
